import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { RequireAuth } from "@/auth/RequireAuth";
import { Landing } from "@/routes/Landing";
import { Login } from "@/routes/Login";
import { AuthCallback } from "@/routes/AuthCallback";
import { WorkspaceLayout } from "@/components/AppShell/WorkspaceLayout";
import { ChatSurface } from "@/components/Chat/ChatSurface";
import { Plugins } from "@/routes/workspace/Plugins";
import { EmbedKeys } from "@/routes/workspace/EmbedKeys";
import { Schedules } from "@/routes/workspace/Schedules";
import { Batches } from "@/routes/workspace/Batches";
import { Meetings } from "@/routes/workspace/Meetings";
import { Knowledge } from "@/routes/workspace/Knowledge";
import { Skills } from "@/routes/workspace/Skills";
import { SkillsMarketplace } from "@/routes/workspace/SkillsMarketplace";
import { Projects } from "@/routes/workspace/Projects";
import { ProjectRoute } from "@/routes/workspace/ProjectRoute";
import { Dashboard } from "@/routes/workspace/Dashboard";
import { Publisher } from "@/routes/workspace/Publisher";
import { OrganizationSettings } from "@/routes/workspace/OrganizationSettings";
import { NotFound } from "@/routes/NotFound";
import { OnboardingLayout } from "@/routes/onboarding/OnboardingLayout";
import { ContextStep } from "@/routes/onboarding/ContextStep";
import { WorkspaceStep } from "@/routes/onboarding/WorkspaceStep";
import { PlanStep } from "@/routes/onboarding/PlanStep";
import { ToolsStep } from "@/routes/onboarding/ToolsStep";
import { Reactivate } from "@/routes/Reactivate";
import { AdminLayout } from "@/routes/admin/AdminLayout";
import { AdminDashboard } from "@/routes/admin/Dashboard";
import { AdminUsers } from "@/routes/admin/Users";
import { AdminOrgs } from "@/routes/admin/Orgs";
import { AdminAudit } from "@/routes/admin/AuditLog";
import { AdminPlatformAdmins } from "@/routes/admin/PlatformAdmins";
import { AdminPolicies } from "@/routes/admin/Policies";

const router = createBrowserRouter([
  // Public marketing landing (index.html port). Hosts the LoginModal.
  { path: "/", element: <Landing /> },
  // Direct /login still works for bookmarks / fallback redirect targets.
  { path: "/login", element: <Login /> },
  // Post-login landing for the BFF cookie flow.
  { path: "/signed-in", element: <AuthCallback /> },
  // Authenticated workspace. WorkspaceLayout mounts the providers + sidebar
  // once; children swap only the <Outlet/> content, so navigating never
  // remounts the sidebar or loses chat state.
  {
    element: (
      <RequireAuth>
        <WorkspaceLayout />
      </RequireAuth>
    ),
    children: [
      { path: "/workspace", element: <ChatSurface /> },
      { path: "/workspace/plugins", element: <Plugins /> },
      { path: "/workspace/embed-keys", element: <EmbedKeys /> },
      { path: "/workspace/schedules", element: <Schedules /> },
      { path: "/workspace/batches", element: <Batches /> },
      { path: "/workspace/knowledge", element: <Knowledge /> },
      { path: "/workspace/meetings", element: <Meetings /> },
      { path: "/workspace/skills", element: <Skills /> },
      { path: "/workspace/marketplace", element: <SkillsMarketplace /> },
      { path: "/workspace/connections", element: <Navigate to="/workspace/plugins" replace /> },
      { path: "/workspace/projects", element: <Projects /> },
      { path: "/workspace/projects/:id", element: <ProjectRoute /> },
      { path: "/workspace/dashboard", element: <Dashboard /> },
      { path: "/workspace/publisher", element: <Publisher /> },
      { path: "/workspace/organization", element: <OrganizationSettings /> },
    ],
  },
  // Onboarding wizard — nested routes under a shared layout with progress indicator
  {
    path: "/onboard",
    element: <RequireAuth><OnboardingLayout /></RequireAuth>,
    children: [
      { index: true, element: <Navigate to="context" replace /> },
      { path: "context", element: <ContextStep /> },
      { path: "workspace", element: <WorkspaceStep /> },
      { path: "plan", element: <PlanStep /> },
      { path: "tools", element: <ToolsStep /> },
    ],
  },
  // Reactivation screen for lapsed subscriptions
  { path: "/reactivate", element: <RequireAuth><Reactivate /></RequireAuth> },
  // Platform admin console — gated by is_platform_admin in AdminLayout
  {
    path: "/admin",
    element: <RequireAuth><AdminLayout /></RequireAuth>,
    children: [
      { index: true, element: <AdminDashboard /> },
      { path: "users", element: <AdminUsers /> },
      { path: "orgs", element: <AdminOrgs /> },
      { path: "billing", element: <AdminDashboard /> }, // TODO: dedicated billing page
      { path: "platform-admins", element: <AdminPlatformAdmins /> },
      { path: "policies", element: <AdminPolicies /> },
      { path: "audit", element: <AdminAudit /> },
    ],
  },
  // Catch-all 404. Public.
  { path: "*", element: <NotFound /> },
]);

export function App() {
  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}
